import { isConditionGroup } from "../types/rules.js";
import { STEP_LABELS, stepsForStage } from "../step-stage.js";
/** Human phrase for each fire moment — the "when" lead-in of a generated
 *  sentence. Exported so other automation-listing UI (e.g. the E-041 list's
 *  moment chip) shares this vocabulary instead of maintaining a second copy. */
export const MOMENT_PHRASE = {
    keying: "As the operator works",
    on_validate: "At validation",
    step_enter: "Entering a step",
    pre_export: "At export",
    batch_classified: "When a batch is classified",
    batch_complete: "When a batch completes",
    scheduled: "On a schedule",
};
/** Comparators that read as dates get date words when the value is a
 *  relative-date; everything else uses the generic vocabulary. */
const OPERATOR_WORDS = {
    equals: "is", not_equals: "is not",
    greater_than: "is greater than", less_than: "is less than",
    greater_or_equal: "is at least", less_or_equal: "is at most",
    contains: "contains", not_contains: "does not contain",
    starts_with: "starts with", ends_with: "ends with",
    in: "is one of", not_in: "is not one of",
    is_empty: "is empty", is_not_empty: "is not empty",
    matches_regex: "matches pattern", between: "is between",
};
const DATE_OPERATOR_WORDS = {
    greater_than: "is after", less_than: "is before",
    greater_or_equal: "is on or after", less_or_equal: "is on or before",
};
function valuePhrase(value) {
    switch (value.type) {
        case "literal": return String(value.value);
        case "field": return value.name;
        case "list": return value.values.join(", ");
        case "lookup": return `${value.table}.${value.column}`;
        case "constant": return `the constant ${value.name}`;
        case "expression": return `(${value.formula})`;
        case "relative-date":
            if (value.days === 0)
                return "today";
            return value.days > 0
                ? `${value.days} days from today`
                : `${-value.days} days ago`;
    }
}
/** Builder follow-ups (item e) — human phrase per condition source. Used to
 *  read the bare `source.type` string ("document"/"batch"/"confidence"/
 *  "field_group") for every non-field/non-expression source, e.g. "batch is
 *  after 2026-01-01" instead of naming what property/aggregate the condition
 *  actually inspects. */
const FIELD_GROUP_AGGREGATE_PHRASE = {
    count_checked: "the count checked",
    count_filled: "the count filled",
    sum: "the sum",
    any: "any checked",
    all: "all checked",
    none: "none checked",
};
function sourcePhrase(source) {
    switch (source.type) {
        case "field": return source.name;
        case "expression": return `(${source.formula})`;
        case "document": return `the document's ${source.property}`;
        case "batch": return `the batch's ${source.property}`;
        case "user":
            return source.property === "roles" ? "the acting user's roles" : `the acting user's ${source.property}`;
        case "confidence":
            return source.scope === "field"
                ? `${source.name ?? "the field"}'s confidence`
                : "any field's confidence";
        case "field_group":
            return `${FIELD_GROUP_AGGREGATE_PHRASE[source.aggregate]} of ${source.names.join(", ")}`;
        case "lookup":
            return `${source.table}.${source.returnField} (matching ${source.matchField} = ${source.matchValue})`;
    }
}
function conditionPhrase(c) {
    const source = sourcePhrase(c.source);
    if (c.operator === "is_empty" || c.operator === "is_not_empty") {
        return `${source} ${OPERATOR_WORDS[c.operator]}`;
    }
    const words = c.value.type === "relative-date"
        ? (DATE_OPERATOR_WORDS[c.operator] ?? OPERATOR_WORDS[c.operator])
        : OPERATOR_WORDS[c.operator];
    return `${source} ${words} ${valuePhrase(c.value)}`;
}
export function describeCondition(g) {
    if (!g || g.conditions.length === 0)
        return "always";
    const parts = g.conditions.map((c) => isConditionGroup(c) ? `(${describeCondition(c)})` : conditionPhrase(c));
    return parts.join(g.logic === "and" ? " and " : " or ");
}
const OUTCOME_PHRASE = {
    route_to_queue: (c) => `route to ${stepWord(c.targetStep)}`,
    hold: () => "hold the document",
    escalate: () => "escalate",
    reject: () => "reject the document",
    skip_step: (c) => (c.targetStep ? `skip ${stepWord(c.targetStep)}` : "skip the next step"),
    reclassify: (c) => c.targetClassification ? `reclassify as ${String(c.targetClassification)}` : "reclassify the document",
    notify: (c) => {
        // B2 — role and user are a UNION, not XOR (spec §4/§6/§10: "role members'
        // emails ∪ target user's email"). Both may be authored at once, so both
        // must show up in the sentence: role phrase alone, "a teammate" alone, or
        // "<Role> and a teammate" when both are set.
        const hasRole = typeof c.targetRole === "string" && c.targetRole;
        const hasUser = typeof c.targetUserId === "string" && c.targetUserId;
        const target = hasRole
            ? hasUser ? `${roleWord(c.targetRole)} and a teammate` : roleWord(c.targetRole)
            : hasUser ? "a teammate" : roleWord(c.targetRole);
        return `notify ${target}${c.sendEmail === true ? " — in-app and by email" : ""}`;
    },
    reprioritize: (c) => typeof c.priority === "number" ? `change priority to ${c.priority}` : "change priority",
    set_field: (c) => {
        const f = typeof c.targetField === "string" && c.targetField ? c.targetField : "a field";
        const v = JSON.stringify(String(c.value ?? ""));
        return c.mode === "append" ? `add ${v} to ${f}` : `set ${f} to ${v}`;
    },
};
function stepWord(step) {
    const label = typeof step === "string"
        ? STEP_LABELS[step] : undefined;
    return label ?? (typeof step === "string" && step ? step : "another step");
}
function roleWord(role) {
    if (typeof role !== "string" || !role)
        return "a role";
    // `role` may be a stable slug (new authoring) or a display name (legacy). We
    // have no DB here, so render a friendly label from the token itself:
    // de-slugify (split on `-`/whitespace) and title-case each word. A slug like
    // "senior-operator" reads "Senior Operator"; a plain name is unchanged.
    return role
        .split(/[-\s]+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
}
function branchOutcome(b) {
    if (b.family === "check") {
        return b.severity === "error" ? "block the operator" : "warn the operator";
    }
    return b.actions
        .map((a) => OUTCOME_PHRASE[a.effectKey]?.(a.config) ?? a.effectKey.replace(/_/g, " "))
        .join(" and ");
}
function branchSentence(b) {
    return `if ${describeCondition(b.condition)}, ${branchOutcome(b)}`
        .replace(/^if always, /, "always, ");
}
function stageLabels(stages) {
    return stages.map((stage) => {
        const steps = stepsForStage(stage);
        return steps.map((s) => STEP_LABELS[s]).join("/");
    });
}
function whenPhrase(a) {
    const hasRouteBranch = a.branches.some((b) => b.family === "route");
    // 4b (E-062) — the two step-identity moments phrase their step scoping for
    // route-family automations: "Entering Key Entry" / the honest "Entering any
    // step" (F-054), and "When accepted at Key Entry" (R2). Check automations
    // keep the classic "At <steps>" phrasing (they fire at those stations).
    if (hasRouteBranch && a.moment === "step_enter") {
        return a.stages?.length
            ? `Entering ${stageLabels(a.stages).join(" or ")}`
            : "Entering any step";
    }
    if (hasRouteBranch && a.moment === "on_validate") {
        return a.stages?.length
            ? `When accepted at ${stageLabels(a.stages).join(" or ")}`
            : "When accepted at any step";
    }
    if (a.stages?.length) {
        return `At ${stageLabels(a.stages).join(" and ")}`;
    }
    return MOMENT_PHRASE[a.moment];
}
const OVERRIDE_SUFFIX = {
    block: " Cannot be overridden.",
    escalate: " Escalates on override.",
};
export function describeAutomation(a) {
    const when = whenPhrase(a);
    const branches = a.branches.map(branchSentence);
    const body = branches.length === 1
        ? `${when}, ${branches[0]}.`
        : `${when}: ${branches.join("; ")}.`;
    // warn-only automations have no override story (E-033) — an override suffix
    // (of ANY behavior) only makes sense when at least one branch blocks, so a
    // blocking branch is the single precondition for any suffix at all.
    let suffix = "";
    if (a.branches.some((b) => b.severity === "error")) {
        if (a.overrideBehavior === "override") {
            // Who may override — name the restricted roles when the automation
            // carries overrideRoles (finish-promised-runtimes item 1).
            const restricted = a.overrideConfig?.overrideRoles ?? [];
            const by = restricted.length ? ` by ${restricted.map(roleWord).join(" or ")}` : "";
            suffix = a.overrideConfig?.requireReason
                ? ` Overridable${by} with a reason.` : ` Overridable${by}.`;
        }
        else if (a.overrideBehavior) {
            suffix = OVERRIDE_SUFFIX[a.overrideBehavior] ?? "";
        }
    }
    return body + suffix;
}
//# sourceMappingURL=describe-automation.js.map